import React from 'react';
import { Button } from '@/components/ui/button';
import LazyImage from '@/components/ui/LazyImage';
import { Phone, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ServiceHeroProps {
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  beforeImage: string;
  afterImage: string;
}

const ServiceHero = ({ title, subtitle, description, icon, beforeImage, afterImage }: ServiceHeroProps) => {
  const navigate = useNavigate();

  return (
    <section className="relative bg-gradient-to-br from-ventura-blue to-ventura-green min-h-[60vh] flex items-center text-white">
      <div className="absolute inset-0 bg-black/20"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex justify-start mb-6">
              {icon}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              {title}
            </h1>
            
            <h2 className="text-2xl md:text-3xl mb-6 opacity-90">
              {subtitle}
            </h2>
            
            <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl">
              {description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="bg-white text-ventura-blue hover:bg-gray-100 text-lg px-8 py-6"
                onClick={() => window.location.href = "tel:+18054647166"}
              >
                <Phone className="mr-2" size={20} />
                Call Us Now
              </Button>
              
              <Button 
                className="bg-ventura-green text-white hover:bg-green-600 text-lg px-8 py-6 border border-white/20"
                onClick={() => navigate('/#contact')}
              >
                <Calendar className="mr-2" size={20} />
                Get Free Estimate
              </Button>
            </div>
          </div>
          
          <div className="lg:pl-8">
            <div className="before-after-container">
            <div className="before-after-image">
              <LazyImage 
                src={beforeImage} 
                alt={`Before professional ${title.toLowerCase()} service showing dirty surfaces in Ventura County`}
                className="w-full h-80 object-cover rounded-xl shadow-2xl"
              />
              <span className="before-after-label before-label">Before</span>
            </div>
            
            <div className="before-after-image">
              <LazyImage 
                src={afterImage} 
                alt={`After eco-friendly ${title.toLowerCase()} showing clean spotless results in Ventura CA`}
                className="w-full h-80 object-cover rounded-xl shadow-2xl"
              />
              <span className="before-after-label after-label">After</span>
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceHero;